<script setup lang="ts">
import { type CardLabel, CardLabelColorMaps } from '@itinerary/types';
import type { IconConfig } from '#imports';

interface IProps {
    label: CardLabel;
    icons?: IconConfig[];
    large?: boolean;
    maxWidth?: string;
}
const { label, icons = [], large, maxWidth = '125px' } = defineProps<IProps>();
const sizer = ref<HTMLDivElement>();
const width = ref('0');
const height = ref('0');
const iconSize = computed(() => (large ? 'small' : 'x-small'));
const color = computed(
    () =>
        label.color ||
        CardLabelColorMaps[label.backgroundColor!] ||
        'var(--gray-900)'
);
const borderColor = computed(() => {
    if (label.backgroundColor?.includes('var')) {
        const color = Number(
            /var\(--([a-z]*-)*(\d+)\)/.exec(label.backgroundColor!)![2]
        );
        const newColorRate = color < 700 ? color + 100 : color;
        return label.backgroundColor.replace(`${color}`, `${newColorRate}`);
    } else {
        return label.backgroundColor;
    }
});
const iconList = computed<IconConfig[]>(() => {
    const iconList: IconConfig[] = [];
    if (label.icon) {
        iconList.push({
            icon: label.icon,
            color: color.value,
        });
    }
    return [
        ...iconList,
        ...icons.map(i => ({
            ...i,
            color: i.color || color.value,
        })),
    ];
});

onMounted(() => {
    width.value = `${sizer.value?.offsetWidth || 0}px`;
    height.value = `${sizer.value?.offsetHeight || 0}px`;
});
</script>

<template>
    <div
        class="label-root"
        :style="{
            '--background': label.backgroundColor,
            '--border-color': borderColor,
            '--color': color,
        }"
        @click.stop
    >
        <div ref="sizer" class="label-view-container" :class="{ large }">
            <mr-icon
                v-for="(icon, key) in iconList"
                :key
                :icon="icon.icon"
                :color="icon.color"
                :size="iconSize"
            />
            <span v-if="label.title" class="title">
                {{ label.title }}
            </span>
        </div>
        <div class="fake" />
    </div>
</template>
<style lang="scss" scoped>
.label-root {
    position: relative;
    max-width: v-bind(maxWidth);
    width: fit-content;
    .label-view-container {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 28px;
        border-radius: 12px;
        background-color: var(--background, usePalette($p-cool-gray, 100));
        border: 1px solid var(--border-color);
        padding: 8px;
        width: fit-content;
        gap: 4px;
        white-space: nowrap;
        max-width: v-bind(maxWidth);
        .title {
            text-overflow: ellipsis;
            overflow: hidden;
        }
        &.large {
            height: 32px;
            .title {
                @include tg-m($w5, var(--color));
                text-align: right;
            }
        }
        .title {
            @include tg-s($w5, var(--color));
            text-align: right;
        }
    }
    .fake {
        position: absolute;
        top: 0;
        right: 0;
        visibility: hidden;
        width: v-bind(width);
        height: v-bind(height);
    }
    &:hover,
    &:active {
        .label-view-container {
            @include shadow(3);
            position: absolute;
            top: 0;
            right: 0;
            z-index: 1000;
            max-width: unset;
            .title {
                text-overflow: unset;
                overflow: unset;
            }
        }
        .fake {
            position: static;
        }
    }
}
</style>
